import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import * as signalR from '@microsoft/signalr'
import { format } from 'date-fns'
import { StateContext } from 'src/state'
import * as roleService from 'src/services/roleService'
import * as consoleProcessamentoService from 'src/services/consoleProcessamentoService'
import * as minhasEmpresasService from 'src/services/minhasEmpresasService'
import { CONSOLE_HUB_URL } from 'src/constants/config'
import {
  Wrapper,
  ToggleButton,
  Panel,
  PanelHeader,
  HeaderTabs,
  HeaderActions,
  Body,
  FiltersPanel,
  FiltersPanelHeader,
  FilterGroup,
  FilterLabel,
  FilterActions,
  TerminalArea,
  TerminalLine,
} from './styles'

// Console de processamento em tempo real — cobre DAS, Pró-labore, Guia, Baixa e Email
// (ver console.md no ft-ftec-back-docker). Escopo por empresa: histórico e eventos ao vivo só
// mostram a empresa atualmente selecionada (mesma regra do backend, que grava o histórico
// no Redis com chave console:log:{IDEmpresa}:{data}).

const TIPOS = [
  { value: '', label: 'Todos' },
  { value: 'DAS', label: 'DAS' },
  { value: 'PROLABORE', label: 'Pró-labore' },
  { value: 'GUIA', label: 'Guia' },
  { value: 'BAIXA', label: 'Baixa' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'NF', label: 'Importação de NF' },
]

const NIVEIS = [
  { value: '', label: 'Todos' },
  { value: 'OK', label: 'OK' },
  { value: 'PROCESSANDO', label: 'Processando' },
  { value: 'ERRO', label: 'Erro' },
]

const MAX_LINHAS = 500

const selectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: 30,
    background: '#12141c',
    borderColor: '#2a2d37',
    fontSize: 12,
  }),
  menu: (provided) => ({ ...provided, background: '#181a20', fontSize: 12, zIndex: 20 }),
  singleValue: (provided) => ({ ...provided, color: '#e5e7eb' }),
  input: (provided) => ({ ...provided, color: '#e5e7eb' }),
  placeholder: (provided) => ({ ...provided, color: '#6b7280' }),
  option: (provided, state) => ({
    ...provided,
    background: state.isFocused ? '#2a2d37' : '#181a20',
    color: '#e5e7eb',
  }),
  indicatorSeparator: (provided) => ({ ...provided, background: '#2a2d37' }),
}

function hoje() {
  return format(new Date(), 'yyyy-MM-dd')
}

function filtroPadrao(idEmpresa, empresaLabel) {
  return { idEmpresa: idEmpresa || null, empresaLabel: empresaLabel || '', data: hoje(), servico: '', nivel: '', busca: '' }
}

function ConsoleProcessamento() {
  const [{ user, company }] = useContext(StateContext)

  const idEmpresaLogada = company && company.data && company.data.ID
  const empresaLogadaLabel = company && company.data && company.data.Apelido

  const [isOpen, setIsOpen] = useState(false)
  const [isExpandido, setIsExpandido] = useState(false)
  const [carregando, setCarregando] = useState(false)
  const [conectado, setConectado] = useState(false)
  const [eventos, setEventos] = useState([])
  const [filtrosEdicao, setFiltrosEdicao] = useState(() => filtroPadrao(idEmpresaLogada, empresaLogadaLabel))
  const [filtrosAplicados, setFiltrosAplicados] = useState(() => filtroPadrao(idEmpresaLogada, empresaLogadaLabel))
  const [empresas, setEmpresas] = useState({ items: [], loading: false, loaded: false })

  const terminalRef = useRef(null)
  const idEmpresaRef = useRef(filtrosAplicados.idEmpresa)
  useEffect(() => {
    idEmpresaRef.current = filtrosAplicados.idEmpresa
  }, [filtrosAplicados.idEmpresa])

  const podeVer = user.logged && roleService.roleIsContadorRootAdmin(user)

  const carregarEmpresas = useCallback(async () => {
    if (empresas.loading || empresas.loaded) return
    setEmpresas((atual) => ({ ...atual, loading: true }))
    try {
      const response = await minhasEmpresasService.getEmpresas(user.data.Token)
      setEmpresas({ items: response.Lista || [], loading: false, loaded: true })
    } catch (error) {
      setEmpresas({ items: [], loading: false, loaded: false })
    }
  }, [empresas.loading, empresas.loaded, user])

  const carregarHistorico = useCallback(async (idEmpresaAlvo, dataAlvo) => {
    if (!idEmpresaAlvo) return
    setCarregando(true)
    try {
      const historico = await consoleProcessamentoService.getHistorico(idEmpresaAlvo, dataAlvo)
      setEventos(Array.isArray(historico) ? historico.slice(-MAX_LINHAS) : [])
    } catch (error) {
      setEventos([])
    } finally {
      setCarregando(false)
    }
  }, [])

  // Conecta ao hub só enquanto o painel está aberto — evita conexão SignalR ociosa em todas
  // as abas de todos os usuários o tempo todo. Não depende de qual empresa está selecionada no
  // filtro: eventos de todas as empresas chegam pelo mesmo canal, o filtro por empresa é feito
  // no cliente (ver idEmpresaRef dentro do handler ReceberEvento) — trocar de empresa no filtro
  // não precisa reconectar.
  useEffect(() => {
    if (!isOpen) return undefined

    if (!CONSOLE_HUB_URL) {
      // eslint-disable-next-line no-console
      console.warn('[ConsoleProcessamento] CONSOLE_HUB_URL vazio — confira REACT_APP_ADMIN_API no .env do frontend.')
      return undefined
    }

    // eslint-disable-next-line no-console
    console.log(`[ConsoleProcessamento] conectando em ${CONSOLE_HUB_URL}...`)

    if (filtrosAplicados.idEmpresa) {
      carregarHistorico(filtrosAplicados.idEmpresa, filtrosAplicados.data)
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(CONSOLE_HUB_URL, {
        // Mesmo AccessToken (Auth0) usado no header Authorization das chamadas REST — o
        // navegador não consegue setar header custom no handshake de WebSocket, então o
        // signalR client manda via querystring "?access_token=". O Hub tem [Authorize] e o
        // backend só aceita esse token (ver Auth0TokenValidationEvents.OnMessageReceived).
        accessTokenFactory: () => user.data.AccessToken,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build()

    connection.on('ReceberEvento', (mensagemBruta) => {
      try {
        const evento = JSON.parse(mensagemBruta)
        if (evento.IDEmpresa !== idEmpresaRef.current) return
        setEventos((atual) => [...atual, evento].slice(-MAX_LINHAS))
      } catch (error) {
        // mensagem malformada — ignora, não bloqueia o console
      }
    })

    connection.onreconnected(() => {
      // eslint-disable-next-line no-console
      console.log('[ConsoleProcessamento] reconectado.')
      setConectado(true)
    })
    connection.onclose((error) => {
      // eslint-disable-next-line no-console
      console.warn('[ConsoleProcessamento] conexão encerrada.', error)
      setConectado(false)
    })

    connection
      .start()
      .then(() => {
        // eslint-disable-next-line no-console
        console.log('[ConsoleProcessamento] conectado com sucesso.')
        setConectado(true)
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[ConsoleProcessamento] falha ao conectar:', error)
        setConectado(false)
      })

    return () => {
      connection.stop()
      setConectado(false)
    }
    // filtrosAplicados só importa aqui pra carga inicial ao abrir — mudanças de empresa/data
    // depois disso são tratadas por aplicarFiltros/limparFiltros, não precisam reconectar o hub.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight
    }
  }, [eventos])

  const eventosFiltrados = useMemo(() => {
    const busca = (filtrosAplicados.busca || '').toLowerCase().trim()
    return eventos.filter((evento) => {
      if (filtrosAplicados.servico && evento.Tipo !== filtrosAplicados.servico) return false
      if (filtrosAplicados.nivel && evento.Status !== filtrosAplicados.nivel) return false
      if (busca && !(evento.Mensagem || '').toLowerCase().includes(busca)) return false
      return true
    })
  }, [eventos, filtrosAplicados])

  const aplicarFiltros = useCallback(() => {
    const precisaRecarregar =
      filtrosEdicao.data !== filtrosAplicados.data ||
      filtrosEdicao.idEmpresa !== filtrosAplicados.idEmpresa
    setFiltrosAplicados(filtrosEdicao)
    if (precisaRecarregar) {
      carregarHistorico(filtrosEdicao.idEmpresa, filtrosEdicao.data)
    }
  }, [filtrosEdicao, filtrosAplicados, carregarHistorico])

  const limparFiltros = useCallback(() => {
    const padrao = filtroPadrao(idEmpresaLogada, empresaLogadaLabel)
    setFiltrosEdicao(padrao)
    setFiltrosAplicados(padrao)
    carregarHistorico(padrao.idEmpresa, padrao.data)
  }, [carregarHistorico, idEmpresaLogada, empresaLogadaLabel])

  const limparTela = useCallback(() => setEventos([]), [])

  const baixarLog = useCallback(() => {
    const linhas = eventosFiltrados.map(
      (evento) =>
        `${evento.Timestamp} ${evento.Status} [${evento.Tipo}] empresa=${evento.IDEmpresa} ${evento.Mensagem}`
    )
    const blob = new Blob([linhas.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `console-processamento-${filtrosAplicados.data}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }, [eventosFiltrados, filtrosAplicados.data])

  const qtdFiltrosAtivos =
    ['servico', 'nivel', 'busca'].filter((chave) => filtrosAplicados[chave]).length +
    (filtrosAplicados.idEmpresa !== idEmpresaLogada ? 1 : 0)

  if (!podeVer) return null

  return (
    <Wrapper>
      {!isOpen && (
        <ToggleButton onClick={() => setIsOpen(true)} title="Abrir console de processamento">
          <span>&gt;_</span>
        </ToggleButton>
      )}

      {isOpen && (
        <Panel expandido={isExpandido}>
          <PanelHeader>
            <HeaderTabs>
              <span className="tab active">Terminal</span>
              <span
                className={`status-dot ${conectado ? 'on' : 'off'}`}
                title={conectado ? 'Conectado' : 'Desconectado'}
              />
            </HeaderTabs>
            <HeaderActions>
              <button onClick={limparTela} title="Limpar tela (só localmente)">
                Limpar
              </button>
              <button onClick={baixarLog} title="Baixar log visível">
                Download
              </button>
              <button
                onClick={() => setIsExpandido((valorAtual) => !valorAtual)}
                title={isExpandido ? 'Recolher' : 'Expandir'}>
                {isExpandido ? '⤡' : '⤢'}
              </button>
              <button onClick={() => setIsOpen(false)} title="Fechar">
                ✕
              </button>
            </HeaderActions>
          </PanelHeader>

          <Body>
            <FiltersPanel>
              <FiltersPanelHeader>
                Filtros do Terminal
                {qtdFiltrosAtivos > 0 && <span className="badge">{qtdFiltrosAtivos}</span>}
              </FiltersPanelHeader>

              <FilterGroup>
                <FilterLabel>Empresa</FilterLabel>
                <Select
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  placeholder="Buscar empresa..."
                  isLoading={empresas.loading}
                  onMenuOpen={carregarEmpresas}
                  options={empresas.items}
                  getOptionValue={(option) => option.ID}
                  getOptionLabel={(option) =>
                    option.Identificacao ? `${option.Apelido} - ${option.Identificacao}` : option.Apelido
                  }
                  value={
                    empresas.items.find((item) => item.ID === filtrosEdicao.idEmpresa) || {
                      ID: filtrosEdicao.idEmpresa,
                      Apelido: filtrosEdicao.empresaLabel || `Empresa ${filtrosEdicao.idEmpresa || ''}`,
                    }
                  }
                  onChange={(option) =>
                    setFiltrosEdicao({
                      ...filtrosEdicao,
                      idEmpresa: option.ID,
                      empresaLabel: option.Apelido,
                    })
                  }
                  noOptionsMessage={() => (empresas.loading ? 'Carregando...' : 'Sem opções')}
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Data</FilterLabel>
                <input
                  type="date"
                  value={filtrosEdicao.data}
                  onChange={(evento) =>
                    setFiltrosEdicao({ ...filtrosEdicao, data: evento.target.value })
                  }
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Serviço</FilterLabel>
                <select
                  value={filtrosEdicao.servico}
                  onChange={(evento) =>
                    setFiltrosEdicao({ ...filtrosEdicao, servico: evento.target.value })
                  }>
                  {TIPOS.map((tipo) => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </option>
                  ))}
                </select>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Nível de log</FilterLabel>
                <select
                  value={filtrosEdicao.nivel}
                  onChange={(evento) =>
                    setFiltrosEdicao({ ...filtrosEdicao, nivel: evento.target.value })
                  }>
                  {NIVEIS.map((nivel) => (
                    <option key={nivel.value} value={nivel.value}>
                      {nivel.label}
                    </option>
                  ))}
                </select>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Busca</FilterLabel>
                <input
                  type="text"
                  placeholder="Buscar por palavra-chave..."
                  value={filtrosEdicao.busca}
                  onChange={(evento) =>
                    setFiltrosEdicao({ ...filtrosEdicao, busca: evento.target.value })
                  }
                />
              </FilterGroup>

              <FilterActions>
                <button className="secondary" onClick={limparFiltros}>
                  Limpar
                </button>
                <button className="primary" onClick={aplicarFiltros}>
                  Aplicar filtros
                </button>
              </FilterActions>
            </FiltersPanel>

            <TerminalArea ref={terminalRef}>
              {carregando && <div className="linha-sistema">Carregando histórico...</div>}
              {!carregando && eventosFiltrados.length === 0 && (
                <div className="linha-sistema">Nenhum evento para os filtros selecionados.</div>
              )}
              {eventosFiltrados.map((evento, indice) => (
                <TerminalLine key={indice} nivel={evento.Status}>
                  <span className="ts">{evento.Timestamp}</span>{' '}
                  <span className="nivel">{evento.Status === 'ERRO' ? 'ERROR' : 'INFO'}</span>{' '}
                  <span className="tipo">[{evento.Tipo}]</span>{' '}
                  <span className="empresa">empresa={evento.IDEmpresa}</span>{' '}
                  <span className="mensagem">{evento.Mensagem}</span>
                </TerminalLine>
              ))}
            </TerminalArea>
          </Body>
        </Panel>
      )}
    </Wrapper>
  )
}

export default ConsoleProcessamento
